import getOr from 'lodash/fp/getOr';
import join from 'lodash/fp/join';
import { ErrorResponse, InMemoryWebStorage, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { config } from '../../config';
import { mapUserProfile } from './userProfile';

const trace = import.meta.env.DEV ? (...args) => console.log('[oidcLogin]', ...args) : () => {};

const pullLocale = getOr('en-GB', 'profile.locale');

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const isErrorResponse = error => error instanceof ErrorResponse;

const retrySigninSilent = (oauthConfig, userManager) => {
    userManager.signinSilent().catch(error => {
        trace('oidc.signinSilent failed!', error);
        if (isErrorResponse(error) && error.error === 'login_required') {
            trace('oidc.sessionExpired');
            oauthConfig.onSessionExpired();
        } else {
            trace('oidc.schedulingRetry');
            setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
        }
    });
};

export const adaptPublishedInfo = (result = {}) => ({
    accessToken: result.access_token,
    expiresInSeconds: result.expires_in,
    idToken: result.profile,
    locale: pullLocale(result),
    profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
    const redirectUri = config.login.redirectUri;
    const silentRedirectUri = config.login.silentRedirectUri;

    const settings = {
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', config.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        staleStateAge: 600,
        monitorSession: true,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    };

    trace('oidc.auth.settings', settings);

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig, userManager) => {
    userManager.events.addUserLoaded(user => {
        trace('oidc.signinSilent success!');
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(error => {
        trace('oidc.silentRenewError', error);
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut((...args) => {
        trace('oidc.userSignedOut', ...args);
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

export const configureMockUserManager = ({ onSessionRenewed }, accessToken) => {
    console.warn('[feature/login/oidc-session] Using mocked authorization due to config setting');

    const signinSilent = () => {
        onSessionRenewed(
            adaptPublishedInfo({
                access_token: accessToken || 'valid-mocked-oauth-bogus-token',
                expires_in: 60 * 60 * 24 * 365,
                profile: {
                    account: 'mockaccount',
                    azp: 'test-client',
                    email: 'test@example.com',
                    family_name: 'Client',
                    given_name: 'Test',
                    locale: config.login.mockLocale,
                    name: 'Test Client',
                    sub: 'prod-rio-users:mock-user',
                    tenant: 'mock-tenant',
                },
            })
        );
        return Promise.resolve();
    };

    const clearStaleState = () => {
        console.info('[feature/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    return { signinSilent, clearStaleState };
};
